import type { AssessmentProctoringConfig } from '@really-assess/shared/assessments'
import {
  DEFAULT_ASSESSMENT_PROCTORING,
  DEFAULT_TIME_LIMIT_CONFIG,
} from '@really-assess/shared/proctoring'
import { ProctoringSliderInput } from './proctoring-config-inputs'

type ScoringWeightKey = keyof AssessmentProctoringConfig['scoringWeights']
type ViolationPenaltyKey = keyof AssessmentProctoringConfig['violationPenalties']

interface ProctoringConfigFieldsProps {
  value: AssessmentProctoringConfig
  onChange: (value: AssessmentProctoringConfig) => void
  /** Admin form uses compact styling; proctoring-test uses dark panel styling */
  variant?: 'admin' | 'test'
  showScoringHelp?: boolean
}

function patch(
  current: AssessmentProctoringConfig,
  patchValue: Partial<AssessmentProctoringConfig>
): AssessmentProctoringConfig {
  return {
    ...current,
    ...patchValue,
    scoringWeights: patchValue.scoringWeights
      ? { ...current.scoringWeights, ...patchValue.scoringWeights }
      : current.scoringWeights,
    violationPenalties: patchValue.violationPenalties
      ? { ...current.violationPenalties, ...patchValue.violationPenalties }
      : current.violationPenalties,
    timeLimit: patchValue.timeLimit
      ? { ...current.timeLimit, ...patchValue.timeLimit }
      : current.timeLimit,
  }
}

export function ProctoringConfigFields({
  value,
  onChange,
  variant = 'admin',
  showScoringHelp = variant === 'test',
}: ProctoringConfigFieldsProps) {
  const compact = variant === 'admin'
  const defaults = DEFAULT_ASSESSMENT_PROCTORING
  const timeLimit = value.timeLimit ?? DEFAULT_TIME_LIMIT_CONFIG

  const setWeight = (key: ScoringWeightKey, weight: number) => {
    onChange(
      patch(value, {
        scoringWeights: { ...value.scoringWeights, [key]: weight },
      })
    )
  }

  const setPenalty = (key: ViolationPenaltyKey, penalty: number) => {
    onChange(
      patch(value, {
        violationPenalties: { ...value.violationPenalties, [key]: penalty },
      })
    )
  }

  const weightSum =
    value.scoringWeights.identity +
    value.scoringWeights.presence +
    value.scoringWeights.behavior

  return (
    <div className={compact ? 'space-y-6' : 'space-y-6'}>
      {showScoringHelp && (
        <section className="bg-gray-900 rounded-lg p-3">
          <h3 className="text-sm font-medium text-blue-400 mb-2">How the Score is Calculated</h3>
          <div className="text-xs text-gray-300 space-y-2">
            <p className="font-mono bg-gray-800 p-2 rounded">
              Score = (Identity × Weight) + (Presence × Weight) + (Behavior × Weight)
            </p>
            <ul className="space-y-1 text-gray-400">
              <li><span className="text-white">Identity</span> = Face similarity % (0-100)</li>
              <li><span className="text-white">Presence</span> = 100 if face visible, 0 if not</li>
              <li><span className="text-white">Behavior</span> = 100 minus violation penalties</li>
            </ul>
            <p className="text-yellow-400 text-xs mt-2">
              Weights should add up to 100% for accurate scoring.
            </p>
          </div>
        </section>
      )}

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Timing</h3>
        <div className="space-y-4">
          <ProctoringSliderInput
            compact={compact}
            label="Verification Interval"
            description="How often to check the camera. Lower = more checks, higher CPU usage."
            value={value.verificationIntervalMs}
            onChange={(v) => onChange(patch(value, { verificationIntervalMs: v }))}
            min={500}
            max={10000}
            step={100}
            unit="ms"
            defaultValue={defaults.verificationIntervalMs}
          />
          <ProctoringSliderInput
            compact={compact}
            label="No Face Warning Delay"
            description="How long to wait before warning when no face is visible."
            value={value.noFaceWarningThresholdMs}
            onChange={(v) => onChange(patch(value, { noFaceWarningThresholdMs: v }))}
            min={500}
            max={10000}
            step={100}
            unit="ms"
            defaultValue={defaults.noFaceWarningThresholdMs}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Completion Poll Interval"
            description="How often the client checks for external completion signals."
            value={value.completionPollIntervalMs}
            onChange={(v) => onChange(patch(value, { completionPollIntervalMs: v }))}
            min={1000}
            max={30000}
            step={500}
            unit="ms"
            defaultValue={defaults.completionPollIntervalMs}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Thresholds</h3>
        <div className="space-y-4">
          <ProctoringSliderInput
            compact={compact}
            label="Similarity Threshold"
            description="Minimum face match % to verify identity. Lower = more lenient."
            value={value.identitySimilarityThreshold}
            onChange={(v) => onChange(patch(value, { identitySimilarityThreshold: v }))}
            min={0}
            max={100}
            step={1}
            unit="%"
            defaultValue={defaults.identitySimilarityThreshold}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Max Consecutive Failures"
            description="Failed identity checks in a row before suspension."
            value={value.maxConsecutiveIdentityFailures}
            onChange={(v) => onChange(patch(value, { maxConsecutiveIdentityFailures: v }))}
            min={1}
            max={20}
            step={1}
            unit=""
            defaultValue={defaults.maxConsecutiveIdentityFailures}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Passing Score"
            description="Minimum integrity score needed to pass."
            value={value.passingScore}
            onChange={(v) => onChange(patch(value, { passingScore: v }))}
            min={0}
            max={100}
            step={1}
            unit="%"
            defaultValue={defaults.passingScore}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
          Scoring Weights
          <span className="ml-2 text-xs text-gray-500 normal-case">(Sum: {weightSum.toFixed(2)})</span>
        </h3>
        <div className="space-y-4">
          <ProctoringSliderInput
            compact={compact}
            label="Identity Weight"
            description="Weight of face similarity match in final score."
            value={value.scoringWeights.identity}
            onChange={(v) => setWeight('identity', v)}
            min={0}
            max={1}
            step={0.05}
            unit=""
            format={(v) => `${(v * 100).toFixed(0)}%`}
            defaultValue={defaults.scoringWeights.identity}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Presence Weight"
            description="Weight of face being visible in camera."
            value={value.scoringWeights.presence}
            onChange={(v) => setWeight('presence', v)}
            min={0}
            max={1}
            step={0.05}
            unit=""
            format={(v) => `${(v * 100).toFixed(0)}%`}
            defaultValue={defaults.scoringWeights.presence}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Behavior Weight"
            description="Weight of good behavior (no violations)."
            value={value.scoringWeights.behavior}
            onChange={(v) => setWeight('behavior', v)}
            min={0}
            max={1}
            step={0.05}
            unit=""
            format={(v) => `${(v * 100).toFixed(0)}%`}
            defaultValue={defaults.scoringWeights.behavior}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Violation Penalties</h3>
        <div className="space-y-4">
          <ProctoringSliderInput
            compact={compact}
            label="No Face"
            description="Penalty when face is not visible."
            value={value.violationPenalties.noFace}
            onChange={(v) => setPenalty('noFace', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.noFace}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Multiple Faces"
            description="Penalty when more than one face is detected."
            value={value.violationPenalties.multipleFaces}
            onChange={(v) => setPenalty('multipleFaces', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.multipleFaces}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Identity Mismatch"
            description="Penalty when face does not match enrolled face."
            value={value.violationPenalties.identityMismatch}
            onChange={(v) => setPenalty('identityMismatch', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.identityMismatch}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Tab Switch"
            description="Penalty when user switches browser tab."
            value={value.violationPenalties.tabSwitch}
            onChange={(v) => setPenalty('tabSwitch', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.tabSwitch}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Window Blur"
            description="Penalty when the assessment window loses focus."
            value={value.violationPenalties.windowBlur}
            onChange={(v) => setPenalty('windowBlur', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.windowBlur}
          />
          <ProctoringSliderInput
            compact={compact}
            label="Fullscreen Exit"
            description="Penalty when user exits fullscreen mode."
            value={value.violationPenalties.fullscreenExit}
            onChange={(v) => setPenalty('fullscreenExit', v)}
            min={0}
            max={10}
            step={0.1}
            unit="pts"
            defaultValue={defaults.violationPenalties.fullscreenExit}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">Time Limit (optional)</h3>
        <p className="text-xs text-gray-500 mb-4">
          Timer starts when the ReallyAvatar iframe loads. Learners receive warnings, then fail automatically when time runs out.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-300 mb-4">
          <input
            type="checkbox"
            checked={timeLimit.enabled}
            onChange={(e) =>
              onChange(patch(value, { timeLimit: { ...timeLimit, enabled: e.target.checked } }))
            }
            className="rounded"
          />
          Enable assessment time limit
        </label>
        {timeLimit.enabled && (
          <div className="grid sm:grid-cols-2 gap-4">
            <NumberField
              label="Total duration (minutes)"
              value={Math.round(timeLimit.durationMs / 60_000)}
              onChange={(minutes) =>
                onChange(
                  patch(value, {
                    timeLimit: { ...timeLimit, durationMs: minutes * 60_000 },
                  })
                )
              }
              min={1}
              max={480}
            />
            <NumberField
              label="First warning when (minutes left)"
              value={Math.round(timeLimit.firstWarningAtRemainingMs / 60_000)}
              onChange={(minutes) =>
                onChange(
                  patch(value, {
                    timeLimit: { ...timeLimit, firstWarningAtRemainingMs: minutes * 60_000 },
                  })
                )
              }
              min={1}
            />
            <NumberField
              label="Repeat warning every (minutes)"
              description="0 = only the first warning"
              value={Math.round(timeLimit.warningIntervalMs / 60_000)}
              onChange={(minutes) =>
                onChange(
                  patch(value, {
                    timeLimit: { ...timeLimit, warningIntervalMs: minutes * 60_000 },
                  })
                )
              }
              min={0}
            />
            <label className="flex items-center gap-2 text-sm text-gray-300 sm:mt-6">
              <input
                type="checkbox"
                checked={timeLimit.showCountdown}
                onChange={(e) =>
                  onChange(
                    patch(value, { timeLimit: { ...timeLimit, showCountdown: e.target.checked } })
                  )
                }
                className="rounded"
              />
              Show countdown to learner
            </label>
          </div>
        )}
      </section>
    </div>
  )
}

function NumberField({
  label,
  description,
  value,
  onChange,
  min,
  max,
}: {
  label: string
  description?: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
}) {
  return (
    <div>
      <label className="block text-sm text-gray-300 mb-1">{label}</label>
      {description && <p className="text-xs text-gray-500 mb-1">{description}</p>}
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full rounded-lg px-3 py-2 text-white text-sm field-input border border-assess-border bg-transparent"
      />
    </div>
  )
}

export function validateProctoringConfig(config: AssessmentProctoringConfig): string | null {
  const timeLimit = config.timeLimit ?? DEFAULT_TIME_LIMIT_CONFIG
  if (!timeLimit.enabled) return null

  const durationMinutes = Math.round(timeLimit.durationMs / 60_000)
  const firstWarningMinutes = Math.round(timeLimit.firstWarningAtRemainingMs / 60_000)

  if (durationMinutes <= 0) {
    return 'Time limit must be greater than 0 minutes.'
  }
  if (firstWarningMinutes <= 0 || firstWarningMinutes >= durationMinutes) {
    return 'First warning must be between 1 minute and less than the total time limit.'
  }
  if (timeLimit.warningIntervalMs < 0) {
    return 'Warning interval cannot be negative.'
  }
  return null
}
